import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { gsap } from "gsap";

export default function About() {
  const [activeSection, setActiveSection] = useState("mission");
  const [isAnimating, setIsAnimating] = useState(false);
  const { t } = useTranslation();
  const contentRef = useRef(null);
  const sliderBarRef = useRef(null);
  const selectContainerRef = useRef(null);
  const missionRef = useRef(null);
  const visionRef = useRef(null);
  const histoireRef = useRef(null);
  const isInitializedRef = useRef(false);
  const activeSectionRef = useRef(activeSection);

  const content = {
    mission: t("about.mission"),
    vision: t("about.vision"),
    histoire: t("about.history"),
  };

  const updateSliderPosition = (section, immediate = false) => {
    let targetRef;
    switch (section) {
      case "mission":
        targetRef = missionRef;
        break;
      case "vision":
        targetRef = visionRef;
        break;
      case "histoire":
        targetRef = histoireRef;
        break;
      default:
        return;
    }

    if (
      !targetRef.current ||
      !sliderBarRef.current ||
      !selectContainerRef.current
    )
      return;

    const targetElement = targetRef.current;
    const container = selectContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    const left = targetRect.left - containerRect.left;
    const width = targetRect.width;

    if (immediate) {
      // Position immédiate pour l'initialisation
      gsap.set(sliderBarRef.current, {
        left: `${left}px`,
        width: `${width}px`,
        opacity: 1,
      });
    } else {
      // Animation fluide pour les changements
      gsap.to(sliderBarRef.current, {
        left: `${left}px`,
        width: `${width}px`,
        duration: 0.4,
        ease: "power2.out",
      });
    }
  };

  const handleSectionChange = (newSection) => {
    if (isAnimating || activeSection === newSection) return;

    setIsAnimating(true);

    // Animation de la barre
    updateSliderPosition(newSection);

    // Animation de fade out
    gsap.to(contentRef.current, {
      opacity: 0,
      duration: 0.2,
      ease: "power2.out",
      onComplete: () => {
        setActiveSection(newSection);

        // Animation de fade in après le changement de contenu
        gsap.to(contentRef.current, {
          opacity: 1,
          duration: 0.2,
          ease: "power2.out",
          onComplete: () => {
            setIsAnimating(false);
          },
        });
      },
    });
  };

  // Mettre à jour la ref chaque fois que activeSection change
  useEffect(() => {
    activeSectionRef.current = activeSection;
    // Si activeSection change de "mission", cela signifie que l'utilisateur a interagi
    // On marque comme initialisé pour empêcher le setTimeout initial de réinitialiser
    if (!isInitializedRef.current && activeSection !== "mission") {
      isInitializedRef.current = true;
    }
  }, [activeSection]);

  useEffect(() => {
    // Animation initiale au montage
    gsap.fromTo(
      contentRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.5, ease: "power2.out" }
    );

    // Position initiale de la barre après un court délai pour s'assurer que le DOM est rendu
    // Utiliser activeSectionRef pour éviter la closure et obtenir la valeur actuelle
    const timer = setTimeout(() => {
      // Ne réinitialiser que si l'initialisation n'a pas encore été faite
      // Cela évite de réinitialiser si l'utilisateur a cliqué avant que le timeout ne se termine
      if (!isInitializedRef.current) {
        // Utiliser la valeur actuelle de activeSection via la ref pour éviter la closure
        updateSliderPosition(activeSectionRef.current, true);
        isInitializedRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Mettre à jour la position de la barre lors du redimensionnement
  useEffect(() => {
    const handleResize = () => {
      updateSliderPosition(activeSection);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeSection]);

  return (
    <div className="Service-about">
      <div className="Service-about-Select" ref={selectContainerRef}>
        <div className="Service-about-SliderBar" ref={sliderBarRef}></div>
        <h2
          ref={missionRef}
          className={activeSection === "mission" ? "mission active" : "mission"}
          onClick={() => handleSectionChange("mission")}
          style={{ textAlign: "left" }}
        >
          <span>{t("about.titleMission")}</span>
        </h2>
        <h2
          ref={visionRef}
          className={activeSection === "vision" ? "vision active" : "vision"}
          onClick={() => handleSectionChange("vision")}
        >
          <span>{t("about.titleVision")}</span>
        </h2>
        <h2
          ref={histoireRef}
          className={
            activeSection === "histoire" ? "histoire active" : "histoire"
          }
          onClick={() => handleSectionChange("histoire")}
        >
          <span>{t("about.titleHistory")}</span>
        </h2>
      </div>

      <div
        ref={contentRef}
        className={`Service-about-Content ${activeSection}`}
      >
        <p>{content[activeSection]}</p>
      </div>
    </div>
  );
}
